import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Users, Wifi, WifiOff, MoreVertical, MessageCircle, X, Settings } from 'lucide-react'
import { useP2PStore } from '../../store/p2pStore'
import { ConnectionWizard } from './ConnectionWizard'
import { toastManager, TOAST_GROUPS } from '../../utils/toastManager'

export function ConnectionsPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddConnection, setShowAddConnection] = useState(false)
  const [showConnectionWizard, setShowConnectionWizard] = useState(false)
  const [connectionInput, setConnectionInput] = useState('')
  const [isConnecting, setIsConnecting] = useState(false)

  // Direct store access for maximum stability (avoid multiple hook calls)
  const store = useP2PStore()
  const {
    connections,
    connectToPeer,
    connectByAlias,
    disconnectFromPeer,
    setSelectedPeerForChat,
    restoreNetworkState,
    localSelfta,
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    networkError,
    connectionManager,
    autoReinitializeNetworkIfNeeded
  } = store

  // Convert connections Map to Array and derive connection status
  const connectionsArray = Array.from(connections.values())
  const connectionStatus = {
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    connectionCount: connectionsArray.length,
    error: networkError
  }

  // CRITICAL FIX: Auto-reinitialize network if needed for persistent connections
  useEffect(() => {
    let mounted = true

    const checkNetworkState = async () => {
      if (localSelfta && !connectionManager && !isOnline && !isInitializing) {
        console.log('🔄 ConnectionsPage: Detected stored avatar without network - attempting auto-reinitialization...')

        try {
          const success = await autoReinitializeNetworkIfNeeded()
          if (mounted && success) {
            console.log('✅ ConnectionsPage: Network auto-reinitialized successfully')
          } else if (mounted) {
            console.warn('⚠️ ConnectionsPage: Network auto-reinitialization failed')
          }
        } catch (error) {
          if (mounted) {
            console.error('❌ ConnectionsPage: Error during network auto-reinitialization:', error)
          }
        }
      }
    }

    // Run check after a short delay to allow store rehydration to complete
    const timeoutId = setTimeout(checkNetworkState, 100)

    return () => {
      mounted = false
      clearTimeout(timeoutId)
    }
  }, [localSelfta, connectionManager, isOnline, isInitializing, autoReinitializeNetworkIfNeeded])

  // Restore network state on component mount (legacy - keeping for backwards compatibility)
  useEffect(() => {
    restoreNetworkState()
  }, [restoreNetworkState])

  // CRITICAL FIX: Proper alias resolution from selfta object
  const filteredConnections = connectionsArray.filter(conn => {
    // Primary alias source: conn.selfta.alias, fallback to conn.alias, final fallback to truncated peer ID
    const alias = conn.selfta?.alias || conn.alias || `user_${conn.id.slice(-8)}`
    const displayName = conn.selfta?.displayName || conn.displayName || alias
    const searchLower = searchTerm.toLowerCase()

    return alias.toLowerCase().includes(searchLower) ||
           displayName.toLowerCase().includes(searchLower)
  })

  // Handle connection attempt
  const handleConnect = async () => {
    if (!connectionInput.trim()) {
      toastManager.error('Please enter a peer ID or alias', TOAST_GROUPS.CONNECTION)
      return
    }

    setIsConnecting(true)
    
    try {
      let success = false
      
      // Try connecting by alias first (if it doesn't look like a peer ID)
      if (connectionInput.length < 20 && !connectionInput.includes('-')) {
        success = await connectByAlias(connectionInput.trim())
      }
      
      // If alias connection failed or input looks like peer ID, try peer ID
      if (!success) {
        success = await connectToPeer(connectionInput.trim())
      }
      
      if (success) {
        setShowAddConnection(false)
        setConnectionInput('')
        console.log('Connection established!')
        toastManager.success('Connected!', TOAST_GROUPS.CONNECTION)
      }
    } catch (error) {
      console.error('Connection error:', error)
      toastManager.error('Connection failed', TOAST_GROUPS.CONNECTION)
    } finally {
      setIsConnecting(false)
    }
  }

  // Handle disconnect
  const handleDisconnect = (peerId: string) => {
    const success = disconnectFromPeer(peerId)
    if (success) {
      console.log('Peer disconnected')
      toastManager.info('Disconnected', TOAST_GROUPS.CONNECTION, { icon: '🔌' })
    }
  }

  // Handle chat with peer
  const handleStartChat = (peerId: string) => {
    setSelectedPeerForChat(peerId)
    // Navigate to chat page would happen here
    toastManager.info('Opening chat...', TOAST_GROUPS.CONNECTION, { icon: '💬' })
  }

  const getStatusIcon = (connectionState: string) => {
    return connectionState === 'connected' ? (
      <Wifi className="w-4 h-4 text-success-600" />
    ) : connectionState === 'connecting' ? (
      <div className="w-4 h-4 animate-spin border-2 border-primary-500 border-t-transparent rounded-full" />
    ) : (
      <WifiOff className="w-4 h-4 text-secondary-400" />
    )
  }

  const getQualityColor = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'bg-success-500'
      case 'good': return 'bg-primary-500'
      case 'fair': return 'bg-warning-500'
      case 'poor': return 'bg-error-500'
      default: return 'bg-secondary-500'
    }
  }

  return (
    <div className="min-h-full overflow-x-hidden">
      <div className="p-4 md:p-6 max-w-full">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              Connections
            </h1>
            <p className="text-secondary-600">
              Manage your peer-to-peer connections
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => setShowConnectionWizard(true)}
              className="btn btn-primary whitespace-nowrap mobile-touch-target"
            >
              <Settings className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Connection </span>Wizard
            </button>
            <button
              onClick={() => setShowAddConnection(true)}
              className="btn btn-outline whitespace-nowrap mobile-touch-target"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Quick </span>Add
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 overflow-hidden">
          <div className="flex-1 w-full sm:max-w-md relative min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search connections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 mobile-no-zoom-input mobile-touch-target"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="card">
          <div className="card-content">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 mb-1">
                  Total Connections
                </p>
                <p className="text-2xl font-bold text-secondary-900">
                  {connectionsArray.length}
                </p>
              </div>
              <Users className="w-8 h-8 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 mb-1">
                  Active Now
                </p>
                <p className="text-2xl font-bold text-success-600">
                  {connectionsArray.filter(c => c.connectionState === 'connected').length}
                </p>
              </div>
              <Wifi className="w-8 h-8 text-success-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-secondary-600 mb-1">
                  Offline
                </p>
                <p className="text-2xl font-bold text-secondary-500">
                  {connectionsArray.filter(c => c.connectionState !== 'connected').length}
                </p>
              </div>
              <WifiOff className="w-8 h-8 text-secondary-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Network Status Notice */}
      {localSelfta && !connectionStatus.isOnline && (
        <div className="mb-6 card">
          <div className="card-content">
            <div className="flex items-center justify-between p-4 bg-warning-50 rounded-lg border border-warning-200">
              <div className="flex items-center space-x-3">
                <WifiOff className="w-6 h-6 text-warning-600" />
                <div>
                  <div className="font-medium text-warning-800">
                    P2P Network Disconnected
                  </div>
                  <div className="text-sm text-warning-700">
                    Your avatar ({localSelfta.alias}) is saved but not connected to the network.
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/avatar')}
                className="btn btn-sm btn-warning mobile-touch-target"
              >
                Reconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connections List */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Your Connections</h2>
          <p className="card-description">
            {filteredConnections.length} of {connectionsArray.length} connections
          </p>
        </div>

        <div className="card-content">
          {filteredConnections.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">
                {searchTerm ? 'No connections found' : connectionStatus.isOnline ? 'No connections yet' : 'Network not connected'}
              </h3>
              <p className="text-secondary-600 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : connectionStatus.isOnline
                  ? 'Add your first connection to start chatting'
                  : 'Connect to the P2P network first to manage connections'
                }
              </p>
              {!searchTerm && connectionStatus.isOnline && (
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowConnectionWizard(true)}
                    className="btn btn-primary mobile-touch-target"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Connection Wizard
                  </button>
                  <button
                    onClick={() => setShowAddConnection(true)}
                    className="btn btn-outline mobile-touch-target"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Quick Add
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredConnections.map((connection) => (
                <div key={connection.id} className="flex flex-col sm:flex-row items-start sm:items-center p-4 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors min-w-0 space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-3 min-w-0 flex-1 w-full sm:w-auto">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 bg-secondary-200 rounded-full flex items-center justify-center overflow-hidden">
                        {connection.selfta?.profileImage ? (
                          <img
                            src={connection.selfta.profileImage}
                            alt={`${connection.selfta.displayName || connection.selfta.alias}'s avatar`}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <span className="text-secondary-600 font-medium">
                            {/* CRITICAL FIX: Proper alias resolution for avatar initial */}
                            {(connection.selfta?.alias || connection.alias || `user_${connection.id.slice(-8)}`).charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${getQualityColor(connection.connectionQuality)}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-secondary-900 truncate">
                          {/* CRITICAL FIX: Proper alias resolution - selfta.alias is primary source */}
                          {connection.selfta?.displayName || connection.displayName || connection.selfta?.alias || connection.alias || `User_${connection.id.slice(-8)}`}
                        </span>
                        {getStatusIcon(connection.connectionState)}
                        {/* MOBILE FIX: Show connection debugging info */}
                        {connection.pendingHandshake && (
                          <span className="text-xs text-warning-600 animate-pulse">resolving...</span>
                        )}
                        {connection.hasValidAlias === false && (
                          <span className="text-xs text-orange-600 animate-pulse">alias pending</span>
                        )}
                        {connection.isTyping && (
                          <span className="text-xs text-primary-600 animate-pulse">typing...</span>
                        )}
                      </div>
                      <div className="text-sm text-secondary-600 truncate">
                        {/* CRITICAL FIX: Proper alias display - selfta.alias is primary source */}
                        @{connection.selfta?.alias || connection.alias || `user_${connection.id.slice(-8)}`}
                        {/* MOBILE FIX: Show connection method indicator */}
                        {connection.connectionMethod === 'direct-alias' && (
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-1 rounded">★</span>
                        )}
                        {connection.isMobile && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1 rounded">📱</span>
                        )}
                      </div>
                      <div className="text-xs text-secondary-500 truncate hidden sm:block">
                        {/* MOBILE FIX: Show connection status with alias validation */}
                        {connection.hasValidAlias !== false ? (
                          <>Last seen: {connection.lastSeen ? connection.lastSeen.toLocaleString() : 'Just now'}</>
                        ) : (
                          <span className="text-warning-600">⚠️ Waiting for alias confirmation...</span>
                        )}
                        {/* MOBILE FIX: Show peer ID for debugging if needed */}
                        {process.env.NODE_ENV === 'development' && (
                          <span className="ml-2 text-xs font-mono">{connection.id.slice(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between w-full sm:w-auto sm:justify-end space-x-2 flex-shrink-0">
                    {/* MOBILE FIX: Enhanced connection status with alias validation */}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      connection.connectionState === 'connected' && connection.hasValidAlias !== false
                        ? 'bg-success-100 text-success-800'
                        : connection.connectionState === 'connected' && (connection.pendingHandshake || connection.hasValidAlias === false)
                        ? 'bg-warning-100 text-warning-800'
                        : connection.connectionState === 'connecting'
                        ? 'bg-primary-100 text-primary-800'
                        : 'bg-secondary-100 text-secondary-800'
                    }`}>
                      {connection.hasValidAlias === false ? 'resolving' :
                       connection.pendingHandshake ? 'handshake' :
                       connection.connectionState}
                    </span>

                    <div className="flex items-center space-x-2">
                      {connection.connectionState === 'connected' && (
                        <button
                          onClick={() => handleStartChat(connection.id)}
                          className="p-2 hover:bg-primary-100 rounded-lg text-primary-600 hover:text-primary-700 mobile-touch-target"
                          title="Start chat"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}

                      <div className="relative group">
                        <button className="p-2 hover:bg-secondary-200 rounded-lg mobile-touch-target">
                          <MoreVertical className="w-4 h-4 text-secondary-600" />
                        </button>

                        {/* Mobile-optimized dropdown menu */}
                        <div className="absolute right-0 mt-1 w-36 bg-white border border-secondary-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                          {connection.connectionState === 'connected' && (
                            <button
                              onClick={() => handleDisconnect(connection.id)}
                              className="w-full px-3 py-3 text-left text-sm text-error-600 hover:bg-error-50 first:rounded-t-lg last:rounded-b-lg mobile-touch-target"
                            >
                              Disconnect
                            </button>
                          )}
                          <button className="w-full px-3 py-3 text-left text-sm text-secondary-600 hover:bg-secondary-50 first:rounded-t-lg last:rounded-b-lg mobile-touch-target">
                            View Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Connection Modal */}
      {showAddConnection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mobile-safe-area">
          <div className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md mx-4 mobile-prevent-overflow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Connection</h3>
              <button 
                onClick={() => setShowAddConnection(false)}
                className="p-1 hover:bg-secondary-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-secondary-600 mb-4">
              Connect to a peer by their ID or alias. Peer IDs are long identifiers, while aliases are short, memorable names.
            </p>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Enter peer ID or alias..."
                value={connectionInput}
                onChange={(e) => setConnectionInput(e.target.value)}
                className="input mobile-no-zoom-input mobile-touch-target"
                disabled={isConnecting}
                onKeyPress={(e) => e.key === 'Enter' && handleConnect()}
              />
              <p className="text-xs text-secondary-500 mt-1">
                Example: "alice_2024" (alias) or "peer-abc123-xyz..." (peer ID)
              </p>
            </div>
            
            {connectionStatus.isOnline ? (
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleConnect}
                  disabled={isConnecting || !connectionInput.trim()}
                  className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed mobile-touch-target"
                >
                  {isConnecting ? (
                    <>
                      <div className="w-4 h-4 animate-spin border-2 border-white border-t-transparent rounded-full mr-2" />
                      Connecting...
                    </>
                  ) : (
                    'Connect'
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAddConnection(false)
                    setConnectionInput('')
                  }}
                  className="btn btn-outline flex-1 mobile-touch-target"
                  disabled={isConnecting}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-warning-800">
                  ⚠️ P2P network not initialized. Please go to the Avatar page to initialize your network first.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      </div>

      {/* Connection Wizard */}
      <ConnectionWizard
        isOpen={showConnectionWizard}
        onClose={() => setShowConnectionWizard(false)}
        initialStep={connectionStatus.isOnline ? 'peer-discovery' : 'network-init'}
      />
    </div>
  )
}